import uniqBy from 'lodash/uniqBy';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { switchMap, map, take, filter } from 'rxjs/operators';
import { zip } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BakeriesApiService } from '../api-services/services/bakeries-api.service';
import { DeliveryApiService } from '../api-services/services/delivery-api.service';
import {
  UI_DELIVERY_OPTIONS,
  CartState,
  getCart,
  ICartDeliveryInfoOutlet,
  ICartDeliveryInfoProductType,
  DELIVERY_ORDER_DELIVERY_TYPES
} from '../cart';
import { IBakeryDeliveryInfo } from '../delivery/models';
import { IBakery, PRODUCT_TYPE_STRUCTURE_TYPES } from '@cakeiteasy/web-utils';

@Injectable()
export class DeliveryHelperService {
  constructor(private _cartStore: Store<CartState>,
              private _deliveryApi: DeliveryApiService,
              private _bakeryApi: BakeriesApiService,
              private _translate: TranslateService) {
  }

  public getDeliveryInfo(): Promise<IBakeryDeliveryInfo> {
    return new Promise(resolve => {
      this._cartStore.select(getCart).pipe(
        filter((cart) => Boolean(cart && cart.bakery)),
        switchMap((cart) => {
          const deliveryInfo$ = this._deliveryApi.getDeliveryInfo(cart.bakery.id);
          const bakeryInfo$ = this._bakeryApi.getBakeryById(cart.bakery.id);
          return zip(deliveryInfo$, bakeryInfo$);
        }),
        map(([deliveryInfo, bakery]) => {
          let { payInStoreWebShopOnlyMethod, payInStoreWebShopEnabled, payInStoreMarketplaceOnlyMethod } = bakery;

          if (payInStoreMarketplaceOnlyMethod || payInStoreWebShopEnabled && payInStoreWebShopOnlyMethod) {
            deliveryInfo.deliveryPointsExists = false;
          }

          return {
            ...deliveryInfo,
            ...this.mapBakeryToDeliveryInfo(bakery)
          };
        }),
        take(1)
      )
        .subscribe((res: IBakeryDeliveryInfo) => resolve(res));
    });
  }

  public getOutletsWithUnavailableProducts(outlets: ICartDeliveryInfoOutlet[]) {
    return outlets.filter((o: ICartDeliveryInfoOutlet) => o.unavailableProductTypes && o.unavailableProductTypes.length) || [];
  }

  public getAvailabilityErrorMessage(deliveryInfo: IBakeryDeliveryInfo,
                                     toDoorUnavailableProducts: ICartDeliveryInfoProductType[],
                                     outletsWithUnavailableProducts: ICartDeliveryInfoOutlet[],
                                     availabilityOptions: { isPickUpAvailable?: boolean; isToDoorAvailable?: boolean; }): string {
    const { deliveryPointsExists, outletsExists } = deliveryInfo;
    const { isPickUpAvailable, isToDoorAvailable } = availabilityOptions;

    const noDeliveryOptionsAvailable = (!isPickUpAvailable && !isToDoorAvailable)
      || (!isPickUpAvailable && !deliveryPointsExists)
      || (!isToDoorAvailable && !outletsExists)
      || (!deliveryPointsExists && !outletsExists);

    if (outletsExists && !isPickUpAvailable && isToDoorAvailable && deliveryPointsExists) {
      return this._getNotAvailableProductsMsg(
        this._getUnavailableProductsFromOutlets(outletsWithUnavailableProducts),
        'pickupIsNotAvailable'
      );
    } else if (deliveryPointsExists && !isToDoorAvailable && isPickUpAvailable && outletsExists && toDoorUnavailableProducts.length) {
      return this._getNotAvailableProductsMsg(toDoorUnavailableProducts, 'toDoorIsNotAvailableDueToProducts');
    } else if (noDeliveryOptionsAvailable) {
      return this._translate.instant('orderProcess.delivery.noDeliveryOptionsAvailable.message');
    }
  }

  public isDeliveryToDoor(type: string): boolean {
    return type === UI_DELIVERY_OPTIONS.DELIVERY || type === UI_DELIVERY_OPTIONS.MAIL;
  }

  public isPickup(type: string): boolean {
    return type === UI_DELIVERY_OPTIONS.PICKUP;
  }

  public isGiftDelivery(deliveryType: string): boolean {
    return deliveryType === UI_DELIVERY_OPTIONS.GIFT;
  }

  public determineShippingMethod(type: string, deliveryType: string): string {
    if (this.isPickup(deliveryType)) {
      return 'Pickup';
    }

    if (deliveryType === DELIVERY_ORDER_DELIVERY_TYPES.THIRD_PARTY) {
      return 'Wolt';
    }

    if (deliveryType === DELIVERY_ORDER_DELIVERY_TYPES.GIFT) {
      return 'Gift delivery';
    }

    return 'Delivery to Door';
  }

  private _getUnavailableProductsFromOutlets(
    unavailableOutlets: ICartDeliveryInfoOutlet[]
  ): ICartDeliveryInfoProductType[] {
    const products: ICartDeliveryInfoProductType[] = unavailableOutlets
      .map(o => o.unavailableProductTypes)
      .reduce((result, value) => result.concat(value), []);

    return uniqBy(products, 'id');
  }

  private mapBakeryToDeliveryInfo(bakery: IBakery): Partial<IBakeryDeliveryInfo> {
    const {
      cities, pickupLocations, deliveryToLocations, schedule, deviatingOrderDeadlines,
      thirdPartyDeliveryEnabled, thirdPartyDeliveryAllowedOnWebshop, thirdPartyDeliveryAllowedOnMarketplace,
      country: { currencySymbol: currency }
    } = bakery;
    return {
      cities, currency, pickupLocations, deliveryToLocations, schedule, deviatingOrderDeadlines,
      thirdPartyDeliveryEnabled, thirdPartyDeliveryAllowedOnWebshop, thirdPartyDeliveryAllowedOnMarketplace,
    };
  }

  private _getNotAvailableProductsMsg(products: ICartDeliveryInfoProductType[], translationKey: string): string {
    const { CATERING_MENU } = PRODUCT_TYPE_STRUCTURE_TYPES;
    const mapper = (products: ICartDeliveryInfoProductType[]) => products
      .map(({ name }) => name)
      .join(', ');

    const cateringMenuStr = mapper(products
      .filter(({ structureType }) => structureType === CATERING_MENU));

    const othersProductsStr = mapper(products
      .filter(({ structureType }) => structureType !== CATERING_MENU));

    if (cateringMenuStr && !othersProductsStr) {
      return this._translate
        .instant(`orderProcess.delivery.${translationKey}.messageWithCateringMenu`, {
          cateringMenu: cateringMenuStr
        });
    } else if (cateringMenuStr && othersProductsStr) {
      return this._translate
        .instant(`orderProcess.delivery.${translationKey}.messageWithCateringMenuAndProducts`, {
          cateringMenu: cateringMenuStr, products: othersProductsStr
        });
    }

    return this._translate
      .instant(`orderProcess.delivery.${translationKey}.message`, { products: othersProductsStr });
  }
}
